import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Sparkles, X, Plus, CalendarCheck, AlertTriangle, TrendingUp,
  UserX, Shield, Banknote, CreditCard, Star, LogOut, AlertOctagon,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map lucide icon name → component
const ICON_MAP = {
  'calendar-check': CalendarCheck,
  'alert-triangle': AlertTriangle,
  'trending-up':    TrendingUp,
  'user-x':         UserX,
  'shield':         Shield,
  'banknote':       Banknote,
  'credit-card':    CreditCard,
  'star':           Star,
  'log-out':        LogOut,
  'alert-octagon':  AlertOctagon,
};

const CATEGORY_COLORS = {
  revenue: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
  risk:    'text-red-400 bg-red-500/10 border-red-500/20',
  intel:   'text-violet-400 bg-violet-500/10 border-violet-500/20',
  ops:     'text-sky-400 bg-sky-500/10 border-sky-500/20',
};

export const RuleTemplatesModal = ({ domainId, token, existingTemplateIds, onClose, onSpawned }) => {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [data, setData] = useState(null);
  const [spawning, setSpawning] = useState(null);
  const [filter, setFilter] = useState('all');
  const existing = useMemo(() => new Set(existingTemplateIds || []), [existingTemplateIds]);

  useEffect(() => {
    axios.get(`${API}/admin/voicefabric/rule-templates`, { headers })
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load templates'));
  }, [headers]);

  const spawn = async (tmpl) => {
    setSpawning(tmpl.id);
    try {
      const r = await axios.post(
        `${API}/admin/domains/${domainId}/voicefabric/rule-templates/${tmpl.id}/spawn`,
        { enabled: true },
        { headers },
      );
      toast.success(`Created rule "${r.data.name}"`);
      onSpawned && onSpawned(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to spawn rule');
    } finally {
      setSpawning(null);
    }
  };

  const filtered = (data?.templates || []).filter(t => filter === 'all' || t.category === filter);
  const categoryOptions = [['all', 'All']].concat(
    Object.entries(data?.categories || {}).map(([k, v]) => [k, v.label])
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-[#0D1526] border border-white/10 rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <h2 className="text-white font-semibold flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-400" />
              Rule Templates
            </h2>
            <p className="text-white/40 text-xs mt-0.5">
              One-click rules that work out of the box. You can edit or upgrade the action after spawning.
            </p>
          </div>
          <button onClick={onClose} className="text-white/30 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {/* Category filter */}
        <div className="flex gap-1 px-6 pt-4 pb-2 flex-wrap">
          {categoryOptions.map(([key, label]) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                filter === key
                  ? 'bg-white/10 text-white'
                  : 'bg-white/5 text-white/40 hover:text-white/70'
              }`}
              data-testid={`tpl-filter-${key}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="overflow-y-auto p-6 pt-3">
          {!data ? (
            <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 text-white/20 animate-spin" /></div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {filtered.map(t => {
                const Icon = ICON_MAP[t.icon] || Sparkles;
                const color = CATEGORY_COLORS[t.category] || CATEGORY_COLORS.intel;
                const already = existing.has(t.id);
                return (
                  <div
                    key={t.id}
                    className="bg-white/5 border border-white/10 rounded-xl p-4 hover:border-white/20 transition-colors"
                    data-testid={`tpl-card-${t.id}`}
                  >
                    <div className="flex items-start gap-3 mb-2">
                      <div className={`p-1.5 rounded-lg border ${color} shrink-0`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-white text-sm font-semibold">{t.name}</p>
                        <span className={`text-[10px] uppercase tracking-wider ${color.split(' ')[0]}`}>
                          {t.category}
                        </span>
                      </div>
                    </div>
                    <p className="text-white/50 text-xs mb-3 line-clamp-2">{t.description}</p>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {(t.extract_fields || []).slice(0, 4).map(f => (
                        <span key={f} className="text-[10px] bg-white/5 text-white/40 px-1.5 py-0.5 rounded font-mono">
                          {f}
                        </span>
                      ))}
                    </div>
                    <button
                      onClick={() => spawn(t)}
                      disabled={spawning === t.id || already}
                      data-testid={`tpl-spawn-${t.id}`}
                      className={`w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        already
                          ? 'bg-white/5 text-white/30 cursor-not-allowed'
                          : 'bg-teal-500/20 hover:bg-teal-500/30 text-teal-300 border border-teal-500/20'
                      }`}
                    >
                      {spawning === t.id
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : already
                          ? 'Already added'
                          : <><Plus className="w-3 h-3" /> Add to domain</>}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
